// fleet-generated: do not edit by hand
//
// Copied verbatim  : infra/fleet/artifacts/ui-core/interaction-context.ts
// Parameters       : none — below this header, identical across the fleet
// Destination      : packages/fleet/ui/src/interaction/context.ts
//
// TO CHANGE THIS FILE, edit the canonical copy in infra and re-render. It
// changes for the whole fleet at once. Editing it here is reverted by the
// next render, and the guard in this repo goes red in the meantime:
//
//     cd ../infra && node fleet/fleet.ts render --repo gym-log-app
//     node fleet/fleet.ts check --repo gym-log-app
//
// The generator runs LOCALLY and is never invoked by CI. CI only verifies
// that what is committed matches the hash in .github/fleet-lock.json, which
// also records WHICH infra commit produced it. The commit is deliberately
// not repeated here: one value, one place — and stamping it into every file
// meant a one-line template edit rewrote four files in 28 repos.
import { createMutable } from "solid-js/store";

export interface InteractionState {
  "input.focused": boolean;
  "input.text-cursor": boolean;
  "dialog.open": boolean;
  "nav-drawer.open": boolean;
  "selection.active": boolean;
  "keyboard-active": boolean;
  [flag: string]: boolean;
}

function initialState(): InteractionState {
  return {
    "input.focused": false,
    "input.text-cursor": false,
    "dialog.open": false,
    "nav-drawer.open": false,
    "selection.active": false,
    "keyboard-active": false,
  };
}

export interface InteractionContext {
  readonly state: InteractionState;
  set: (flag: string, value: boolean) => void;
  get: (flag: string) => boolean;
  contribute: (flag: string, token: symbol, value: boolean) => void;
}

export function createInteractionContext(): InteractionContext {
  const state = createMutable<InteractionState>(initialState());
  const contributors = new Map<string, Set<symbol>>();

  const set: InteractionContext["set"] = (flag, value) => {
    state[flag] = value;
  };

  const contribute: InteractionContext["contribute"] = (flag, token, value) => {
    let owners = contributors.get(flag);
    if (!owners) {
      owners = new Set();
      contributors.set(flag, owners);
    }
    if (value) {
      owners.add(token);
    } else {
      owners.delete(token);
    }
    state[flag] = owners.size > 0;
  };

  return {
    state,
    set,
    get: (flag) => state[flag] === true,
    contribute,
  };
}

function isEditableElement(el: Element | null): boolean {
  if (!el) {
    return false;
  }
  const tag = el.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") {
    return true;
  }
  if (el instanceof HTMLElement && el.isContentEditable) {
    return true;
  }
  return false;
}

export function trackFocus(ctx: InteractionContext, target: Document = document): () => void {
  const update = () => {
    const el = target.activeElement;
    const editable = isEditableElement(el);
    ctx.set("input.focused", editable);
    ctx.set("input.text-cursor", editable && el?.tagName !== "SELECT");
  };
  target.addEventListener("focusin", update, { capture: true });
  target.addEventListener("focusout", update, { capture: true });
  update();
  return () => {
    target.removeEventListener("focusin", update, { capture: true });
    target.removeEventListener("focusout", update, { capture: true });
  };
}

const KEYBOARD_NAV_KEYS = new Set([
  "Tab",
  "ArrowUp",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "j",
  "k",
  "h",
  "l",
  " ",
  "Enter",
  "Escape",
  "?",
]);

export function trackKeyboardActivity(
  ctx: InteractionContext,
  target: EventTarget = globalThis,
): () => void {
  const body = typeof document === "undefined" ? undefined : document.body;
  const activate = (event: Event) => {
    if (event instanceof KeyboardEvent && !KEYBOARD_NAV_KEYS.has(event.key)) {
      return;
    }
    ctx.set("keyboard-active", true);
    body?.classList.add("keyboard-navigation");
  };
  const deactivate = () => {
    ctx.set("keyboard-active", false);
    body?.classList.remove("keyboard-navigation");
  };
  target.addEventListener("keydown", activate, { capture: true });
  target.addEventListener("mousedown", deactivate, { capture: true });
  target.addEventListener("pointerdown", deactivate, { capture: true });
  return () => {
    target.removeEventListener("keydown", activate, { capture: true });
    target.removeEventListener("mousedown", deactivate, { capture: true });
    target.removeEventListener("pointerdown", deactivate, { capture: true });
  };
}
